import { useEffect } from "react";
import tippy from "tippy.js";
import useMatchupStore from "../store/MatchupStore";
import { MatchupAPI } from "./matchupApi";

export function initStaticData(staticData) {
  const matchupStore = useMatchupStore.getState();

  matchupStore.setFixtureId(staticData.fixtureId);
  matchupStore.setLeagueSeasonData(staticData.leagueSeason);
  matchupStore.setLeagueSeasonId(staticData.leagueSeason.id);
  matchupStore.setPlayerStats(staticData.playerStats);
  matchupStore.setSportFixtures(staticData.sportFixtures);
  matchupStore.setTeam1(staticData.team1);
  matchupStore.setTeam2(staticData.team2);
}

export const updateMatchupStore = (staticData) => {
  const matchupStore = useMatchupStore.getState();

  matchupStore.setFixtureId(staticData.fixtureId);
  matchupStore.setLeagueSeasonData(staticData.leagueSeason);
  matchupStore.setLeagueSeasonId(staticData.leagueSeason.id);
  matchupStore.setPlayerStats(staticData.playerStats);

  matchupStore.setLastRefreshed(new Date());
  matchupStore.setSportFixtures(staticData.sportFixtures);
  matchupStore.setTeam1(staticData.team1);
  matchupStore.setTeam2(staticData.team2);
};

const randomDelay = (min, max) => {
  const duration = Math.floor(Math.random() * (max - min + 1)) + min;
  return new Promise((resolve) => setTimeout(resolve, duration));
};

export const refreshMatchupData = async () => {
  const matchupStore = useMatchupStore.getState();
  const fixtureId = matchupStore.fixtureId;

  matchupStore.setIsRefreshing(true);

  const delay = randomDelay(500, 1000);

  try {
    const [staticData] = await Promise.all([
      MatchupAPI.fetchMatchupData(fixtureId),
      delay,
    ]);

    updateMatchupStore(staticData);
  } catch (error) {
    console.error("Error refreshing player stats:", error);
  } finally {
    matchupStore.setIsRefreshing(false);
  }
};

export const winningStyles =
  "bg-slate-100 font-semibold dark:bg-gray-800 dark:text-gray-200";
export const losingStyles = "text-gray-700 dark:text-gray-400";

export function isTeamWinning(fixtureTeam, opponent, weights, stat) {
  const value = weights[stat];

  if (value === 0) {
    return false;
  } else if (value > 0) {
    return fixtureTeam.stats[stat] > opponent.stats[stat];
  } else {
    return fixtureTeam.stats[stat] < opponent.stats[stat];
  }
}

export function renderStat(rawStats, statToRender) {
  if (!rawStats) return "-";

  if (statToRender === "score") {
    return `${rawStats.goals}-${rawStats.behinds} (${rawStats.goals * 6 + rawStats.behinds})`;
  } else if (statToRender === "goalAccuracy") {
    if (rawStats.goals + rawStats.behinds === 0) {
      return "-";
    } else if (rawStats.behinds === 0) {
      return "1.000";
    } else {
      const accuracy = rawStats.goals / (rawStats.goals + rawStats.behinds);
      return accuracy.toFixed(3).slice(1);
    }
  } else if (statToRender === "disposalEfficiency") {
    if (rawStats.effectiveDisposals + rawStats.ineffectiveDisposals === 0) {
      return "-";
    } else if (rawStats.ineffectiveDisposals === 0) {
      return "1.000";
    } else {
      const efficiency =
        rawStats.effectiveDisposals /
        (rawStats.effectiveDisposals + rawStats.ineffectiveDisposals);
      return efficiency.toFixed(3).slice(1);
    }
  } else {
    // General case - direct access to the property
    return rawStats[statToRender];
  }
}

export function setupTooltips(containerRef, dependencies = []) {
  useEffect(() => {
    // Set up tooltips when component mounts or updates
    if (containerRef.current) {
      // Destroy any existing tooltips to prevent duplicates
      const tooltipNodes = containerRef.current.querySelectorAll(
        "[data-tippy-content]",
      );
      tooltipNodes.forEach((node) => {
        if (node._tippy) {
          node._tippy.destroy();
        }
      });

      // Initialize tooltips on elements with data-tippy-content
      tippy("[data-tippy-content]", {
        placement: "top",
        arrow: true,
        animation: "shift-away-subtle",
        interactive: false,
        allowHTML: true,
        delay: [50, 0],
        theme: "custom",
        onShow(instance) {
          instance.popper
            .querySelector(".tippy-box")
            .classList.add(
              "bg-gray-800",
              "text-white",
              "dark:bg-gray-700",
              "text-sm",
            );
          instance.popper
            .querySelector(".tippy-arrow")
            .classList.add("text-gray-800", "dark:text-gray-700");
        },
      });
    }
  }, dependencies); // Re-run when dependencies change
}
